// Единый контекст (Ctx) для команд, обработчиков и правил + «хосты» диалогов и уведомлений,
// которые отрисовывает оболочка (ui/Dialogs.tsx, ui/Toasts.tsx).
import { api } from './api';
import { appStore } from './store';
import { registry, type Ctx } from './registry';
import { activeMini, isTextCommand, MINI_OK } from './editor/active';

// ------------------------------------------------------------------------------------------------ диалоги
export interface OpenDialog { key: number; id: string; props: Record<string, unknown>; resolve: (v: unknown) => void }
let dialogs: OpenDialog[] = [];
const dListeners = new Set<() => void>();
let dKey = 0;
export const dialogHost = {
  get: () => dialogs,
  subscribe(fn: () => void) { dListeners.add(fn); return () => { dListeners.delete(fn); }; },
  open<T>(id: string, props: Record<string, unknown> = {}): Promise<T | undefined> {
    if (!registry.dialogs.get(id)) { console.warn('Нет диалога', id); return Promise.resolve(undefined); }
    return new Promise<T | undefined>((resolve) => {
      const d: OpenDialog = { key: ++dKey, id, props, resolve: resolve as (v: unknown) => void };
      dialogs = [...dialogs, d];
      dListeners.forEach((f) => f());
    });
  },
  close(key: number, result?: unknown) {
    const d = dialogs.find((x) => x.key === key);
    dialogs = dialogs.filter((x) => x.key !== key);
    dListeners.forEach((f) => f());
    d?.resolve(result);
    setTimeout(() => appStore.getState().editor?.commands.focus(), 0);
  },
};

// ------------------------------------------------------------------------------------------------ уведомления
export interface Toast { key: number; text: string; kind: 'info' | 'ok' | 'warn' | 'error' }
let toasts: Toast[] = [];
const tListeners = new Set<() => void>();
let tKey = 0;
export const toastHost = {
  get: () => toasts,
  subscribe(fn: () => void) { tListeners.add(fn); return () => { tListeners.delete(fn); }; },
  push(text: string, kind: Toast['kind'] = 'info', ms = 3500) {
    const t = { key: ++tKey, text, kind };
    toasts = [...toasts.slice(-4), t];
    tListeners.forEach((f) => f());
    if (ms > 0) setTimeout(() => toastHost.dismiss(t.key), ms);
  },
  dismiss(key: number) { toasts = toasts.filter((x) => x.key !== key); tListeners.forEach((f) => f()); },
};

// ------------------------------------------------------------------------------------------------ команды
/** Контекст команды: курсор в малом редакторе (лист задания/реферата) - текстовые команды работают в нём (и кнопки
 *  ленты - «Отменить», «Ж», перечисления - показывают его состояние, а не основного текста). */
export function ctxFor(id: string): Ctx {
  const mini = activeMini();
  if (mini && isTextCommand(id) && MINI_OK.has(id)) return { ...ctx, get editor() { return mini; } } as Ctx;
  return ctx;
}

export function runCommand(id: string, arg?: unknown): unknown {
  const spec = registry.commands.get(id);
  if (!spec) { console.warn('Нет команды', id); return undefined; }
  // курсор в малом редакторе (лист задания/реферата): текстовые команды - в нём, остальные основной текст не трогают
  const mini = activeMini();
  let c: Ctx = ctx;
  if (mini && isTextCommand(id)) {
    if (!MINI_OK.has(id)) {
      ctx.toast(`«${spec.title}» - только в основном тексте отчёта (сейчас курсор на листе задания/реферата)`, 'info', 3500);
      return undefined;
    }
    c = { ...ctx, get editor() { return mini; } } as Ctx;
  }
  if (spec.isEnabled && !spec.isEnabled(c)) return undefined;
  try {
    const r = spec.run(c, arg);
    if (r && typeof (r as Promise<unknown>).catch === 'function') {
      // отмена сборки - не ошибка: нейтральное сообщение (отменил администратор - предупреждение)
      (r as Promise<unknown>).catch((e) => e?.code === 'cancelled'
        ? ctx.toast(String(e.message), e.data?.byAdmin ? 'warn' : 'info', 6000)
        : ctx.toast(String(e?.message || e), 'error', 6000));
    }
    return r;
  } catch (e: any) {
    console.error(e);
    ctx.toast(String(e?.message || e), 'error', 6000);
    return undefined;
  }
}

export const ctx: Ctx = {
  get editor() { const e = appStore.getState().editor; return e && !e.isDestroyed ? e : null; },
  store: appStore,
  api,
  openDialog: (id, props) => dialogHost.open(id, props),
  toast: (text, kind, ms) => toastHost.push(text, kind, ms),
  run: runCommand,
};

// уведомления из модулей схемы (без зависимости от ctx): window.dispatchEvent(new CustomEvent('sto-toast', {detail}))
window.addEventListener('sto-toast', (e: Event) => {
  const d = (e as CustomEvent).detail || {};
  if (d.text) toastHost.push(String(d.text), d.kind || 'info', d.ms || 4000);
});
